import React, { useEffect, useState } from "react";
import { DealsPageLayout } from "../deals/dealsPageLayout/DealsPageLayout";
import * as api from "../../api/generated-api";
import { LawFirmClientStatus } from "../../api/generated-api";
import { RouteComponentProps } from "react-router-dom";
import { getDefaultOrganization } from "../../api/organizations";
import {
  getCompanyLawFirms,
  getCompanyMembers,
  updateCompanyLawFirmClient,
} from "../../api/company/company";
import { connect } from "react-redux";
import { configureAuthHeader, configureBaseUrl } from "../../api/axiosConfig";
import { toValidIdentifier } from "../../helpers/utilsTS";
import { Grid, Stack, Typography } from "@mui/material";
import Button from "../common/forms/button";
import { TitledGrid } from "../common/TitledGrid";
import { CompanyMembers } from "./CompanyMembers";
import { CompanyInformationGrid } from "./CompanyInformationGrid";
import { OrganizationName } from "../organization/OrganizationName";

type CompanyInformationPageProps = RouteComponentProps<{
  companyOrgId?: string;
}> & {
  token: string;
};
const CompanyInformationPage = (props: CompanyInformationPageProps) => {
  const companyOrgId = Number(props.match.params.companyOrgId);
  const token = props.token;

  configureBaseUrl();
  configureAuthHeader(token);

  const [currentOrg, setCurrentOrg] = useState<api.Organization>();
  const [lawFirmClients, setLawFirmClients] = useState<api.LawFirmClient[]>([]);
  const [officers, setOfficers] = useState<api.Person[]>();
  const [boardMembers, setBoardMembers] = useState<api.Person[]>();
  const [keyCommonHolders, setKeyCommonHolders] = useState<api.Person[]>();

  const personsFromLegalEntities = (
    legalEntities: api.LegalEntity[]
  ): api.Person[] => {
    if (legalEntities) {
      return legalEntities.map((legalEntity) => legalEntity.person);
    }

    return [];
  };

  const fetchAndSetCompanyLawFirms = () => {
    getCompanyLawFirms(companyOrgId, (fetchedLawFirmClients) => {
      setLawFirmClients(fetchedLawFirmClients);
    });
  };

  useEffect(() => {
    getDefaultOrganization((fetchedOrganization) => {
      setCurrentOrg(fetchedOrganization);
    });

    fetchAndSetCompanyLawFirms();

    getCompanyMembers(companyOrgId, (fetchedMembers) => {
      const fetchedCEOs = fetchedMembers.filter((fetchedMember) => {
        if (
          fetchedMember.legalEntityType ==
          api.LegalEntityType.Chief_Executive_Officer
        ) {
          return fetchedMember;
        }
      });

      const fetchedSecretaries = fetchedMembers.filter((fetchedMember) => {
        if (fetchedMember.legalEntityType == api.LegalEntityType.Secretary) {
          return fetchedMember;
        }
      });

      const fetchedOfficers = [];

      if (fetchedCEOs && !!fetchedCEOs.length) {
        fetchedOfficers.push(fetchedCEOs[0]);
      }

      if (fetchedSecretaries && !!fetchedSecretaries.length) {
        fetchedOfficers.push(fetchedSecretaries[0]);
      }

      setOfficers(personsFromLegalEntities(fetchedOfficers));

      const fetchedBoardMembers = fetchedMembers.filter((fetchedMember) => {
        if (fetchedMember.legalEntityType == api.LegalEntityType.Board_Member) {
          return fetchedMember;
        }
      });

      setBoardMembers(personsFromLegalEntities(fetchedBoardMembers));

      const fetchedKeyCommonHolders = fetchedMembers.filter((fetchedMember) => {
        if (
          fetchedMember.legalEntityType == api.LegalEntityType.Key_Common_Holder
        ) {
          return fetchedMember;
        }
      });

      setKeyCommonHolders(personsFromLegalEntities(fetchedKeyCommonHolders));
    });
  }, [companyOrgId]);

  if (!currentOrg) {
    return null;
  }

  return (
    <DealsPageLayout title="Company Information">
      <CompanyInformationGrid company={currentOrg} />
      <CompanyMembers title={"Company Officers"} people={officers} />
      <CompanyMembers title={"Board Members"} people={boardMembers} />
      <CompanyMembers title={"Key Common Holders"} people={keyCommonHolders} />

      <TitledGrid title="Company Counsel">
        <Grid item xs={6}>
          <Stack>
            {lawFirmClients &&
              lawFirmClients.map((lawFirmClient) => {
                return (
                  <OrganizationName
                    key={lawFirmClient.id}
                    organization={lawFirmClient.lawFirm}
                  />
                );
              })}
          </Stack>
        </Grid>
        <Grid
          item
          xs={6}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {lawFirmClients &&
            lawFirmClients.map((lawFirmClient) => {
              return (
                <Stack key={lawFirmClient.id}>
                  {lawFirmClient.status != LawFirmClientStatus.accepted && (
                    <>
                      <Typography variant={"subtitle2"}>&nbsp;</Typography>
                      <Button
                        data-testid={toValidIdentifier(
                          `${lawFirmClient.lawFirm.name}-client-law-firm-accept-button`
                        )}
                        size="medium"
                        onClick={() => {
                          updateCompanyLawFirmClient(
                            companyOrgId,
                            lawFirmClient.id,
                            { status: api.LawFirmClientStatus.accepted },
                            () => {
                              fetchAndSetCompanyLawFirms();
                            }
                          );
                        }}
                      >
                        Accept
                      </Button>
                    </>
                  )}
                </Stack>
              );
            })}
        </Grid>
      </TitledGrid>
    </DealsPageLayout>
  );
};

const mapStateToProps = (state) => {
  const { token } = state.auth;

  return {
    token,
  };
};

export default connect(mapStateToProps)(CompanyInformationPage);
